import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { FaDownload } from 'react-icons/fa';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  ListSPInvoiceData,
  DownloadSPInvoicePDF,
} from 'reactRedux/thunks/eInvoiceThunk/eInvoiceThunk';
import useListState from 'hooks/useListState';
import useInvoiceDateRange from 'hooks/useInvoiceDateRange';
import SellerInvoiceListFilter from './SellerInvoiceListFilter';

const SalesPartnerInvoiceList = ({ isActive }) => {
  const dispatch = useDispatch();
  const { tabletextColor } = useColorModeVariables();

  const {
    params,
    setParam,
    setParams,
    debouncedParams,
    page,
    setPage,
    resetAll,
  } = useListState({
    defaults: { region: '', name: '', limit: 10 },
    debounceKeys: ['name'],
    prefix: 'sp_',
  });

  const {
    showCalendar,
    setShowCalendar,
    tempDateRange,
    calendarFilterTitle,
    handleSelect,
    handleApply,
    handleClear,
    resetDateRange,
    getFormattedDates,
  } = useInvoiceDateRange(setPage);

  const [downloadingRows, setDownloadingRows] = useState(new Set());

  const { isSpLoading, spInvoiceListData } = useSelector(
    (state) => state.EInvoiceSlice,
  );

  const columnHelper = createColumnHelper();

  const getList = useCallback(() => {
    const { start, end } = getFormattedDates();
    dispatch(
      ListSPInvoiceData({
        page,
        limit: params.limit || 10,
        region: params.region,
        name: debouncedParams.name,
        start,
        end,
      }),
    );
  }, [
    dispatch,
    page,
    params.limit,
    params.region,
    debouncedParams.name,
    getFormattedDates,
  ]);

  useEffect(() => {
    if (isActive) {
      getList();
    }
  }, [getList, isActive]);

  const handleReset = () => {
    resetAll();
    resetDateRange();
  };

  const handleDownload = useCallback(
    (row) => {
      const key = row.id;
      setDownloadingRows((prev) => new Set(prev).add(key));
      dispatch(
        DownloadSPInvoicePDF(
          {
            id: row.id,
            invoiceId: row.invoiceId,
            sellerName: row.sellerName,
            year: row.year,
            quarter: row.quarter || undefined,
            month: row.month || undefined,
          },
          () => {
            setDownloadingRows((prev) => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            });
          },
        ),
      );
    },
    [dispatch],
  );

  const columns = [
    columnHelper.accessor('sellerName', {
      id: 'sellerName',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Seller
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('periodLabel', {
      id: 'periodLabel',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Invoice Period
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('totalCommission', {
      id: 'totalCommission',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Commission Amount
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() !== undefined && info.getValue() !== null
            ? `${info.row.original.currencySymbol ?? '€'}${Number(info.getValue()).toFixed(2)}`
            : '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('invoiceDate', {
      id: 'invoiceDate',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Invoice Date
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" fontWeight="700">
          {info.getValue() ? moment(info.getValue()).format('DD/MM/YYYY') : '-'}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'download',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Download
        </Text>
      ),
      cell: ({ row }) => {
        const rowData = row.original;
        const key = rowData.id;
        const isDownloading = downloadingRows.has(key);
        return (
          <Button
            size="sm"
            py="6px"
            px="12px"
            fontWeight="semibold"
            borderRadius="6px"
            bg="primary"
            color="white"
            _hover={{ bg: 'primary', opacity: 0.8 }}
            leftIcon={<FaDownload size={13} />}
            isLoading={isDownloading}
            onClick={() => handleDownload(rowData)}
          >
            Download
          </Button>
        );
      },
    }),
  ];

  return (
    <Box>
      <SellerInvoiceListFilter
        searchLabel="Seller"
        name={params.name}
        setName={(val) => setParam('name', val)}
        region={params.region}
        setRegion={(val) => setParam('region', val)}
        setPage={setPage}
        setShowCalendar={setShowCalendar}
        showCalendar={showCalendar}
        tempDateRange={tempDateRange}
        handleApply={handleApply}
        handleClear={handleClear}
        handleSelect={handleSelect}
        calendarFilterTitle={calendarFilterTitle}
        handleReset={handleReset}
      />

      <Flex
        direction="column"
        bgColor="white"
        borderRadius="15px"
        p={{ base: '10px', md: '20px' }}
      >
        <CommonTable
          columns={columns}
          data={spInvoiceListData?.list}
          isLoading={isSpLoading}
          isLink={false}
          keyValue="id"
          dynamicWidth={{
            sellerName: '200px',
            periodLabel: '150px',
            totalCommission: '180px',
            invoiceDate: '150px',
            download: '140px',
          }}
        />

        <CommonPagination
          totalCount={spInvoiceListData?.total}
          activePage={page}
          pageCount={Math.ceil(
            spInvoiceListData?.total / spInvoiceListData?.limit,
          )}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      </Flex>
    </Box>
  );
};

SalesPartnerInvoiceList.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

export default SalesPartnerInvoiceList;
